import { create } from "@bufbuild/protobuf";
import { queryOptions, useQuery } from "@tanstack/react-query";

import { useGitpodAPI } from "@/hooks/use-gitpod-api";
import { defaultRefetchLists, defaultRetry, defaultThrowOnError } from "@/queries/errors";
import { autoPaginate } from "@/queries/pagination";
import { keyWithPrincipal } from "@/queries/principal-key";
import { useListRunners, type PlainRunner } from "@/queries/runner-queries";
import { useAuthenticatedUser } from "@/queries/user-queries";
import { RunnerProvider } from "gitpod-next-api/gitpod/v1/runner_configuration_pb";
import { ListAvailableRunnerManagersRequestSchema } from "gitpod-next-api/gitpod/v1/runner_manager_pb";
import type { AvailableRunnerManager } from "gitpod-next-api/gitpod/v1/runner_manager_pb";

// Type for the available runner manager from the API
export type PlainAvailableRunnerManager = AvailableRunnerManager;

// Runner placeholder for showing available runner managers in UI
export type RunnerPlaceholder = {
    name: string; // Will be the runner manager name
    region: string;
    provider: "GITPOD_HOSTED"; // Always Gitpod Hosted for placeholders
    isPlaceholder: true; // Flag to identify placeholders
    runnerManagerId: string;
};

// Query key factories
export const runnerManagerQueryKey = {
    listAvailable: () => keyWithPrincipal(["runnerManagers", "available"]),
    placeholders: () => keyWithPrincipal(["runnerManagers", "placeholders"]),
};

export const useListAvailableRunnerManagersOptions = () => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();

    return queryOptions({
        queryKey: runnerManagerQueryKey.listAvailable(),
        queryFn: async (): Promise<PlainAvailableRunnerManager[]> => {
            if (!user) {
                throw new Error("User not authenticated");
            }

            const availableRunnerManagers = await autoPaginate(
                (params) => api.runnerManagerService.listAvailableRunnerManagers(params),
                create(ListAvailableRunnerManagersRequestSchema),
                (response) => response.runnerManagers,
            );

            return availableRunnerManagers.map(
                (manager) =>
                    ({
                        runnerManagerId: manager.runnerManagerId,
                        name: manager.name,
                        region: manager.region,
                    }) as AvailableRunnerManager,
            );
        },
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
        enabled: !!user,
        gcTime: 1_000 * 60 * 60, // 1 hour
        staleTime: 1_000 * 60 * 5, // 5 minutes

        // refetch on reconnect, window focus, and every 5 minutes
        refetchOnReconnect: "always",
        refetchOnWindowFocus: "always",
        refetchIntervalInBackground: true,
        refetchInterval: defaultRefetchLists,
    });
};

/**
 * Hook to query available runner managers using the actual API endpoint.
 */
export const useListAvailableRunnerManagers = () => {
    const query = useQuery(useListAvailableRunnerManagersOptions());

    return { ...query, availableRunnerManagers: query.data || [] };
};

/**
 * Creates runner placeholders for available runner managers that don't have Ona Cloud runners.
 * These placeholders show users what other runner managers are available for deployment.
 */
const createRunnerPlaceholders = (
    availableRunnerManagers: PlainAvailableRunnerManager[],
    existingRunners: PlainRunner[],
): RunnerPlaceholder[] => {
    // Get set of runner manager IDs that already have Ona Cloud runners
    const usedManagerIds = new Set(
        existingRunners
            .filter((runner) => runner.provider === RunnerProvider.MANAGED && runner.runnerManagerId)
            .map((runner) => runner.runnerManagerId),
    );

    // Create placeholders for unused runner managers
    return availableRunnerManagers
        .filter((manager) => !usedManagerIds.has(manager.runnerManagerId))
        .map((manager) => ({
            runnerManagerId: manager.runnerManagerId,
            region: manager.region,
            name: manager.name,
            provider: "GITPOD_HOSTED" as const,
            isPlaceholder: true,
        }));
};

export const useRunnerPlaceholdersOptions = ({
    availableRunnerManagers,
    allRunners,
    enabled,
}: {
    availableRunnerManagers: PlainAvailableRunnerManager[];
    allRunners: PlainRunner[];
    enabled: boolean;
}) => {
    // Extract only the runner manager IDs that are actually used for placeholders
    const usedManagerIds = allRunners
        .filter((runner) => runner.provider === RunnerProvider.MANAGED && runner.runnerManagerId)
        .map((runner) => runner.runnerManagerId)
        .sort(); // Sort for consistent query key

    return queryOptions({
        queryKey: [
            ...runnerManagerQueryKey.placeholders(),
            { availableManagers: availableRunnerManagers.map((m) => m.runnerManagerId).sort(), usedManagerIds },
        ],
        queryFn: (): RunnerPlaceholder[] => {
            return createRunnerPlaceholders(availableRunnerManagers, allRunners);
        },
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
        staleTime: 1_000 * 60 * 5, // 5 minutes
        gcTime: 1_000 * 60 * 60, // 1 hour
        enabled,

        // refetch on reconnect, window focus, and every 5 minutes
        refetchOnReconnect: "always",
        refetchOnWindowFocus: "always",
        refetchIntervalInBackground: true,
        refetchInterval: defaultRefetchLists,
    });
};

/**
 * Hook to get runner placeholders for available runner managers.
 * These are shown in the UI to indicate what runner managers are available for deployment.
 */
export const useRunnerPlaceholders = () => {
    const {
        availableRunnerManagers = [],
        isLoading: isLoadingManagers,
        error: managersError,
        isError: isManagersError,
    } = useListAvailableRunnerManagers();

    const {
        runners: allRunners = [],
        isLoading: isLoadingRunners,
        error: runnersError,
        isError: isRunnersError,
    } = useListRunners({});

    const query = useQuery(
        useRunnerPlaceholdersOptions({
            availableRunnerManagers,
            allRunners,
            enabled: !isLoadingManagers && !isLoadingRunners && !isManagersError && !isRunnersError,
        }),
    );

    return {
        ...query,
        isLoading: query.isLoading || isLoadingManagers || isLoadingRunners,
        error: query.error || managersError || runnersError,
        runnerPlaceholders: query.data || [],
    };
};
